import { useRef, useState } from 'react';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { Target } from 'typings/api/shareTradeTarget';
import VisibilitySensor from 'react-visibility-sensor';
import { ShareTradeInfo } from 'typings/api/shareTradeChat';
import UserItem from '../UserItem';
import { UserListWrapper } from './styled';

export interface UserItemTarget extends Target {
  is_shared?: boolean;
}

interface UserListProps {
  className?: string;
  defaultCheckedKey?: string;
  defaultSwitchKey?: string;
  sharedDescription?: string;
  data?: UserItemTarget[];
  disableSharedKey?: string[];
  disableSharedUsers?: boolean;
  previewShareTrade: ShareTradeInfo | Record<string, never>;
  isLoading?: boolean;
  onSelectedUserChange?(isSelected: boolean, userTarget: Target): void;
  onScrollToBottom?(): void;
}

const UserList = ({
  className,
  defaultCheckedKey = '',
  defaultSwitchKey = '',
  sharedDescription = 'Already shared to this friend',
  data = [],
  disableSharedKey = [],
  disableSharedUsers = false,
  previewShareTrade = {},
  isLoading = false,
  onSelectedUserChange = () => { },
  onScrollToBottom = () => { },
}: UserListProps) => {
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);

  const getIsDisabled = (target: UserItemTarget): boolean => {
    if (!disableSharedUsers) return false;

    for (let i = 0; i < disableSharedKey.length; i += 1) {
      if (target[disableSharedKey[i]]) return true;
    }

    return false;
  };

  const handleUserChange = (e: CheckboxChangeEvent, userTarget: Target) => {
    const { checked } = e.target;
    onSelectedUserChange(checked, userTarget);
  };

  const handleSwitchChange = (checked: boolean, userTarget: Target) => {
    onSelectedUserChange(true, {
      ...userTarget,
      is_share_value: checked,
    });
  };

  const handleScroll = () => {
    setIsScrolling(true);

    // Clear any previous timeout to avoid multiple triggers
    if (scrollTimeout.current) {
      clearTimeout(scrollTimeout.current);
    }

    // Set a new timeout to reset the scrolling state
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  const handleScrollToBottom = (isVisible: boolean) => {
    if (isVisible) {
      onScrollToBottom();
    }
  };

  return (
    <UserListWrapper className={className} onScroll={handleScroll}>
      {data.map((item, idx) => {
        const isDisabled = getIsDisabled(item);
        return (
          <VisibilitySensor
            onChange={handleScrollToBottom}
            intervalCheck
            scrollCheck
            active={!isLoading && data.length - 1 === idx}
          >
            <UserItem
              key={item.id}
              avatarUrl={item.avatar_url}
              name={item.name}
              alias={item.shortened_name}
              targetType={item.type}
              description={isDisabled ? sharedDescription : item.description}
              isVerified={item.is_verified}
              defaultChecked={item[defaultCheckedKey] || false}
              onCheckChange={(e) => handleUserChange(e, data[idx])}
              disabled={isDisabled}
              defaultSwitchValue={item[defaultSwitchKey] || false}
              previewShareTrade={previewShareTrade as ShareTradeInfo}
              onSwitchChange={(checked) =>
                handleSwitchChange(checked, data[idx])
              }
              isScrolling={isScrolling}
            />
          </VisibilitySensor>
        );
      })}
    </UserListWrapper>
  );
};

export default UserList;
